import { useState, useMemo, useRef, useEffect } from "react";
import { FaPlus, FaGripVertical } from "react-icons/fa";
import SelectedSongsItem from "./SelectedSongsItem";
import QuestionItem from "./QuestionItem";

const SelectedSongs = ({
    newSongs,
    setNewSongs,
    addNewPlaylist,
    newQuestions = [],
    setNewQuestions,
    enableQuestions = false,
    isEditMode = false,
    onSaveEdit = null,
    onCancelEdit = null,
}) => {
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const scrollContainerRef = useRef(null);

    // Create unified list: merge songs and questions based on order
    const unifiedList = useMemo(() => {
        const items = [];

        // Add all songs first
        newSongs.forEach((song, index) => {
            items.push({ type: "song", data: song, index });
        });

        // Add questions at their order positions (or at end if no order)
        // Separate questions with and without order
        const questionsWithOrder = newQuestions.filter(
            (q) => q.order !== undefined
        );
        const questionsWithoutOrder = newQuestions.filter(
            (q) => q.order === undefined
        );

        // Sort questions with order by their order value
        questionsWithOrder.sort((a, b) => a.order - b.order);

        // Insert questions with order in reverse order to avoid index shifting
        for (let i = questionsWithOrder.length - 1; i >= 0; i--) {
            const question = questionsWithOrder[i];
            const order = question.order;
            // Insert after the song at this index (so order 0 means after first song, i.e., at position 1)
            // If order is >= items.length, append at end
            const insertPosition =
                order + 1 <= items.length ? order + 1 : items.length;
            items.splice(insertPosition, 0, {
                type: "question",
                data: question,
                questionId: question.id,
            });
        }

        // Append questions without order at the end (in their original order)
        // These will get an order when songs are dragged after them
        questionsWithoutOrder.forEach((question) => {
            items.push({
                type: "question",
                data: question,
                questionId: question.id,
            });
        });

        return items;
    }, [newSongs, newQuestions]);

    // Auto-scroll to bottom when songs or questions are added
    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop =
                scrollContainerRef.current.scrollHeight;
        }
    }, [newSongs.length, newQuestions.length]);

    const handleDragStart = (index) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const newUnifiedList = [...unifiedList];
        const draggedItem = newUnifiedList[draggedIndex];
        newUnifiedList.splice(draggedIndex, 1);

        // Adjust dropIndex if we removed an item before it
        const adjustedDropIndex =
            draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
        newUnifiedList.splice(adjustedDropIndex, 0, draggedItem);

        // Rebuild songs and questions arrays with updated order
        const updatedSongs = [];
        const updatedQuestions = [];
        let songIndex = -1;

        newUnifiedList.forEach((item) => {
            if (item.type === "song") {
                songIndex++;
                updatedSongs.push(item.data);
            } else if (item.type === "question") {
                // Order is the current songIndex (after which song to insert)
                // This means the question appears after the song at songIndex
                // If songIndex is -1, the question is before all songs (order: -1)
                // If songIndex equals the last song index, question is after all songs
                // Ensure order is always defined (even if it was undefined before)
                updatedQuestions.push({
                    ...item.data,
                    order: songIndex >= 0 ? songIndex : -1,
                });
            }
        });

        setNewSongs(updatedSongs);
        setNewQuestions(updatedQuestions);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    return (
        <div
            style={{
                flexDirection: "column",
                display: "flex",
                width: "100%",
                height: "100%",
                overflow: "hidden",
                padding: "1vh",
                boxSizing: "border-box",
            }}
        >
            <div
                style={{
                    color: "white",
                    width: "100%",
                    textAlign: "left",
                    fontSize: 16,
                    fontWeight: 500,
                    marginBottom: "1vh",
                }}
            >
                Valgte sanger ({newSongs.length})
                {enableQuestions && newQuestions.length > 0 && (
                    <span style={{ color: "#9ca3af", fontSize: 14 }}>
                        {" "}
                        + {newQuestions.length} bonusspørsmål
                    </span>
                )}
            </div>
            <div
                ref={scrollContainerRef}
                style={{
                    flex: 1,
                    flexDirection: "column",
                    display: "flex",
                    width: "100%",
                    backgroundColor: "#161f33",
                    borderRadius: "0.5vh",
                    padding: "1vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                    minHeight: 0,
                    scrollbarWidth: "thin",
                    scrollbarColor: "#4db193 #161f33",
                    gap: "0.2vh",
                }}
            >
                {unifiedList.map((item, i) => (
                    <div
                        key={
                            item.type === "song"
                                ? item.data.title + "newPlaylist" + i
                                : item.data.id + "question" + i
                        }
                        draggable={enableQuestions || item.type === "song"}
                        onDragStart={() => handleDragStart(i)}
                        onDragOver={(e) => handleDragOver(e, i)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, i)}
                        onDragEnd={handleDragEnd}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            opacity: draggedIndex === i ? 0.5 : 1,
                            borderTop:
                                dragOverIndex === i && draggedIndex !== i
                                    ? "2px solid #4db193"
                                    : "none",
                            paddingTop:
                                dragOverIndex === i && draggedIndex !== i
                                    ? "calc(1vh - 2px)"
                                    : "1vh",
                            transition: "opacity 0.2s ease",
                            cursor:
                                enableQuestions || item.type === "song"
                                    ? draggedIndex === i
                                        ? "grabbing"
                                        : "grab"
                                    : "default",
                        }}
                    >
                        {(enableQuestions || item.type === "song") && (
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "grab",
                                    color: "#9ca3af",
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <FaGripVertical size={16} />
                            </div>
                        )}
                        <div
                            style={{
                                minWidth: "30px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 14,
                                fontWeight: 600,
                                color:
                                    item.type === "question"
                                        ? "#fbbf24"
                                        : "#4db193",
                            }}
                        >
                            {i + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                            {item.type === "song" ? (
                                <SelectedSongsItem
                                    newSongs={newSongs}
                                    setNewSongs={setNewSongs}
                                    song={item.data}
                                />
                            ) : (
                                <QuestionItem
                                    question={item.data}
                                    newQuestions={newQuestions}
                                    setNewQuestions={setNewQuestions}
                                />
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <div
                style={{
                    display: "flex",
                    gap: "1vh",
                    width: "100%",
                    justifyContent: "center",
                    marginTop: "1.5vh",
                    flexShrink: 0,
                }}
            >
                {isEditMode && onCancelEdit && (
                    <div
                        onClick={onCancelEdit}
                        style={{
                            width: "40%",
                            backgroundColor: "#6b7280",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: "0.5vh",
                            cursor: "pointer",
                            padding: "1vh",
                            fontSize: 14,
                            fontWeight: 500,
                            color: "white",
                            transition: "background-color 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#4b5563";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#6b7280";
                        }}
                    >
                        Avbryt
                    </div>
                )}
                <div
                    onClick={
                        isEditMode && onSaveEdit ? onSaveEdit : addNewPlaylist
                    }
                    style={{
                        width: isEditMode ? "40%" : "50%",
                        backgroundColor: "#4db193",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        alignSelf: "center",
                        borderRadius: "0.5vh",
                        cursor: "pointer",
                        padding: "1vh",
                        fontSize: 14,
                        fontWeight: 500,
                        color: "white",
                        transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#3d9a7a";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#4db193";
                    }}
                >
                    {isEditMode ? (
                        "Lagre endringer"
                    ) : (
                        <>
                            <FaPlus style={{ marginRight: "0.5vh" }} />
                            Opprett spilleliste
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SelectedSongs;
